import React from "react";
import { createPortal } from "react-dom";
import { GuestCountPropagationPreview } from "../../services/api";

interface GuestCountPropagationModalProps {
  isOpen: boolean;
  onApply: () => void;
  onCancel: () => void;
  previewData: GuestCountPropagationPreview;
  isLoading: boolean;
  error?: string | null;
}

/** Returns Polish plural form for "pozycja/pozycje/pozycji". */
function formatItemsCount(count: number): string {
  if (count === 1) return `1 pozycja`;
  if (count >= 2 && count <= 4) return `${count} pozycje`;
  return `${count} pozycji`;
}

const GuestCountPropagationModal: React.FC<
  GuestCountPropagationModalProps
> = ({ isOpen, onApply, onCancel, previewData, isLoading, error }) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Group items by dayNumber
  const itemsByDay = previewData.items.reduce<
    Record<number, typeof previewData.items>
  >((acc, item) => {
    if (!acc[item.dayNumber]) {
      acc[item.dayNumber] = [];
    }
    acc[item.dayNumber].push(item);
    return acc;
  }, {});

  const sortedDayNumbers = Object.keys(itemsByDay)
    .map(Number)
    .sort((a, b) => a - b);

  /** Format date string (YYYY-MM-DD) as DD.MM.YYYY for display. */
  function formatDate(dateStr: string | null): string | null {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-count-propagation-title"
      aria-describedby="guest-count-propagation-desc"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
      tabIndex={-1}
      ref={dialogRef}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3
          id="guest-count-propagation-title"
          className="text-lg font-semibold mb-4"
        >
          Aktualizacja planu pobytu
        </h3>

        <div className="space-y-4 mb-6">
          <p id="guest-count-propagation-desc" className="text-neutral-700">
            Zmiana liczby gości z {previewData.oldCount} na {previewData.newCount} wpłynie
            na {formatItemsCount(previewData.totalItems)} gastronomicznych:
          </p>

          <div
            className="max-h-96 overflow-y-auto space-y-3"
            aria-label="Lista pozycji do aktualizacji"
          >
            {sortedDayNumbers.map((dayNumber) => {
              const dayItems = itemsByDay[dayNumber];
              const firstItem = dayItems[0];
              const formattedDate = formatDate(firstItem.dayDate);
              return (
                <div key={dayNumber}>
                  <h4 className="text-sm font-medium text-neutral-600 mb-1">
                    {formattedDate
                      ? `Dzień ${dayNumber} (${formattedDate})`
                      : `Dzień ${dayNumber}`}
                  </h4>
                  <ul className="space-y-1">
                    {dayItems.map((item) => (
                      <li key={item.id} className="text-sm text-neutral-800 pl-3">
                        {item.title}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {previewData.skippedItems > 0 && (
            <p className="text-sm text-neutral-500">
              {formatItemsCount(previewData.skippedItems)} pominięto (ręcznie edytowana
              ilość)
            </p>
          )}

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}
        </div>

        <div className="flex flex-col space-y-3">
          <button
            onClick={onApply}
            disabled={isLoading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Aktualizowanie...
              </span>
            ) : (
              "Zmień wszystko"
            )}
          </button>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 disabled:opacity-50"
          >
            Zmień tylko liczbę gości
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default GuestCountPropagationModal;
